interface ParseTimeInputOptions {
  /**
   * When true, a well-formed zero duration (`0`, `0h 0m`, `0:00`) parses to `0`
   * instead of `null`. Use for the approvals approved-hours cell, where approving
   * `0h 0m` is valid. Defaults to false (time entries can't be zero-length).
   */
  allowZero?: boolean;
}

/**
 * Parses a free-form time duration into minutes.
 *
 * Accepts (all case-insensitive, whitespace tolerant):
 *   - `2`, `1.5`, `0.25` — bare numbers interpreted as hours
 *   - `2h`, `1.5h`, `0.5h` — hours suffix
 *   - `30m`, `90m` — minutes suffix
 *   - `1h 30m`, `2h15m` — combined
 *   - `2:30`, `0:45` — HH:MM format
 *
 * Returns null for invalid input. A zero/negative duration returns null unless
 * `allowZero` is set, in which case a well-formed zero returns 0.
 */
export const parseTimeInputToMinutes = (
  raw: string,
  options: ParseTimeInputOptions = {}
): number | null => {
  const { allowZero = false } = options;
  const value = raw.trim().toLowerCase();
  if (!value) return null;

  // A well-formed total is valid when positive, or exactly zero if allowZero.
  const finalizeTotal = (total: number): number | null => {
    if (total > 0) return total;
    return allowZero && total === 0 ? 0 : null;
  };

  // HH:MM
  const colonMatch = value.match(/^(\d+):([0-5]?\d)$/);
  if (colonMatch) {
    const hours = Number(colonMatch[1]);
    const minutes = Number(colonMatch[2]);
    return finalizeTotal(hours * 60 + minutes);
  }

  // Combined h + m: `1h 30m`, `2h15m`, `2h`, `30m`
  const hmMatch = value.match(/^(?:(\d+(?:\.\d+)?)\s*h)?\s*(?:(\d+(?:\.\d+)?)\s*m)?$/);
  if (hmMatch && (hmMatch[1] || hmMatch[2])) {
    const hours = hmMatch[1] ? Number(hmMatch[1]) : 0;
    const minutes = hmMatch[2] ? Number(hmMatch[2]) : 0;
    return finalizeTotal(Math.round(hours * 60 + minutes));
  }

  // Bare number → hours
  const numberMatch = value.match(/^\d+(?:\.\d+)?$/);
  if (numberMatch) {
    const hours = Number(value);
    return finalizeTotal(Math.round(hours * 60));
  }

  return null;
};
